import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { getCompany, storeCompany } from "../../../../shared/data/storage/authStorage";
import { settingsContainer } from "../../di/container";
import {
  CompanySettings,
  GstBacklog,
  GstChecks,
  GstZohoRegistration,
} from "../../domain/entities/CompanySettings";
import { useGstSettings } from "./useGstSettings";
import { UseSettingsFormReturn } from "./useSettingsForm";

export interface UseGstPanelReturn extends UseSettingsFormReturn {
  /**
   * The form, with the freshest Zoho block folded in.
   *
   * Pass THIS to the sections rather than `settings`: it is what makes the
   * date field's "Use Zoho's date" button appear after a Refresh.
   */
  panelSettings: CompanySettings | null;
  zoho: GstZohoRegistration | null;
  backlog: GstBacklog | null;
  refreshingZoho: boolean;
  refreshZoho: () => Promise<void>;
  syncingTaxes: boolean;
  /** Set after a manual tax sync; cleared on the next one. */
  taxSyncNote: string | null;
  syncTaxes: () => Promise<void>;
  /** ZOUR-19: null until the checks have been run at least once. */
  checks: GstChecks | null;
  runningChecks: boolean;
  checksError: string | null;
  runChecks: () => Promise<void>;
  /** ZOUR-19 item 13: true while the GSTIN is being copied from Zoho. */
  adoptingGstin: boolean;
  adoptGstinError: string | null;
  adoptZohoGstin: () => Promise<void>;
}

/**
 * ZOUR-19 — the GST panel's own state, on top of the shared settings form.
 *
 * Three things the generic machinery does not do:
 *
 *  1. REFRESH WITHOUT LOSING EDITS. The Zoho registration block is read live,
 *     and its Refresh must not replace the form's values — someone who has
 *     typed a date and then pressed Refresh has not asked to lose it. So the
 *     refresh hits its own endpoint and is merged over the loaded settings
 *     instead of replacing them.
 *
 *  2. RE-STORE THE COMPANY IN THE BROWSER. This is the reason the GST toggle was
 *     left off this panel in the first place (2026-08-08): `company_gst_enabled`
 *     is baked into the login payload and cached in localStorage, so a toggle
 *     that only wrote the database would appear to do nothing until the user
 *     signed out and back in. Writing the stored company here is what fixes
 *     that, and it covers `gst_enabled_from` too — the review and manual-create
 *     pages read the boundary date from the stored company.
 *
 *  3. RUN THE TAX SYNC ON DEMAND. The server runs it automatically on the
 *     off -> on flip; the button is for catching up afterwards.
 */
export const useGstPanel = (): UseGstPanelReturn => {
  const form = useGstSettings();

  // Null until a Refresh happens; the loaded settings' own block is used until
  // then. Kept separate so a refresh cannot be mistaken for a form edit.
  const [refreshedZoho, setRefreshedZoho] = useState<GstZohoRegistration | null>(null);
  const [refreshedBacklog, setRefreshedBacklog] = useState<GstBacklog | null>(null);
  const [refreshingZoho, setRefreshingZoho] = useState(false);
  const [syncingTaxes, setSyncingTaxes] = useState(false);
  const [taxSyncNote, setTaxSyncNote] = useState<string | null>(null);
  const [checks, setChecks] = useState<GstChecks | null>(null);
  const [runningChecks, setRunningChecks] = useState(false);
  const [checksError, setChecksError] = useState<string | null>(null);
  const [adoptingGstin, setAdoptingGstin] = useState(false);
  const [adoptGstinError, setAdoptGstinError] = useState<string | null>(null);

  const abortRef = useRef<AbortController | null>(null);

  useEffect(
    () => () => {
      abortRef.current?.abort();
    },
    []
  );

  const zoho = refreshedZoho ?? form.settings?.zoho ?? null;
  const backlog = refreshedBacklog ?? form.settings?.backlog ?? null;

  const panelSettings = useMemo<CompanySettings | null>(() => {
    if (!form.settings) {
      return null;
    }

    if (!refreshedZoho && !refreshedBacklog) {
      return form.settings;
    }

    return {
      ...form.settings,
      zoho: refreshedZoho ?? form.settings.zoho,
      backlog: refreshedBacklog ?? form.settings.backlog,
    };
  }, [form.settings, refreshedZoho, refreshedBacklog]);

  /**
   * Keep the company cached in the browser in step with what was just stored.
   *
   * Runs on load as well as after a save, so a company row changed elsewhere
   * (master's sync, another tab) is picked up too. Only writes when something
   * actually differs, so it cannot loop.
   */
  useEffect(() => {
    const values = form.settings?.values;

    if (!values) {
      return;
    }

    const company = getCompany();

    if (!company) {
      return;
    }

    const nextEnabled = values.company_gst_enabled ?? company.companyGstEnabled;
    const rawFrom = values.gst_enabled_from ?? "";
    const nextFrom = rawFrom === "" ? null : rawFrom;

    if (
      company.companyGstEnabled === nextEnabled &&
      (company.gstEnabledFrom ?? null) === nextFrom
    ) {
      return;
    }

    try {
      storeCompany({
        ...company,
        companyGstEnabled: nextEnabled,
        gstEnabledFrom: nextFrom,
      });
    } catch (err) {
      // storeCompany already logs. A browser that will not accept the write
      // (private mode, full quota) must not break the settings page; the user
      // then sees the change after their next sign-in, which is the behaviour
      // this effect exists to improve on, not one it can make worse.
    }
  }, [form.settings]);

  const refreshZoho = useCallback(async (): Promise<void> => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setRefreshingZoho(true);

    try {
      const result = await settingsContainer.getGstZohoStatus().execute(controller.signal);

      if (!controller.signal.aborted) {
        setRefreshedZoho(result.zoho);
        setRefreshedBacklog(result.backlog);
      }
    } catch (err: any) {
      if (!controller.signal.aborted) {
        // Reported in the block itself rather than as a page error: this is a
        // read of somebody else's API, and failing it must not look like the
        // settings page is broken.
        setRefreshedZoho({
          reachable: false,
          registered: null,
          gstin: null,
          registeredOn: null,
          organizationId: zoho?.organizationId ?? null,
          error: err?.message || "Could not reach Zoho Books.",
        });
      }
    } finally {
      if (!controller.signal.aborted) {
        setRefreshingZoho(false);
      }
    }
  }, [zoho]);

  const syncTaxes = useCallback(async (): Promise<void> => {
    setSyncingTaxes(true);
    setTaxSyncNote(null);

    try {
      const result = await settingsContainer.syncGstTaxes().execute();

      setTaxSyncNote(
        `Synced ${result.added} of ${result.total} tax rates from Zoho Books.`
      );
    } catch (err: any) {
      setTaxSyncNote(err?.message || "Could not sync taxes from Zoho Books.");
    } finally {
      setSyncingTaxes(false);
    }
  }, []);

  /**
   * Run the two checks.
   *
   * On demand, not on load: both walk the documents and masters tables, and a
   * settings page that pays for that every visit teaches people not to open
   * it. Keeps the previous result on screen while a re-run is in flight so the
   * list does not blink away under the person reading it.
   */
  const runChecks = useCallback(async (): Promise<void> => {
    setRunningChecks(true);
    setChecksError(null);

    try {
      setChecks(await settingsContainer.runGstChecks().execute());
    } catch (err: any) {
      setChecksError(err?.message || "Could not run the GST checks.");
    } finally {
      setRunningChecks(false);
    }
  }, []);

  /**
   * ZOUR-19 item 13 — copy Zoho's GSTIN into the blank local one.
   *
   * The response is the whole settings payload, so it is fed through the same
   * `accept` path a save uses: the locked GSTIN field fills in, and the
   * server's confirmation arrives as a warning line like any other.
   */
  const adoptZohoGstin = useCallback(async (): Promise<void> => {
    setAdoptingGstin(true);
    setAdoptGstinError(null);

    try {
      const result = await settingsContainer.useZohoGstin().execute();

      form.applyServerSettings(result);
    } catch (err: any) {
      setAdoptGstinError(
        err?.fieldErrors?.gst_number || err?.message || "Could not copy the GSTIN from your books."
      );
    } finally {
      setAdoptingGstin(false);
    }
  }, [form]);

  return {
    ...form,
    panelSettings,
    zoho,
    backlog,
    refreshingZoho,
    refreshZoho,
    syncingTaxes,
    taxSyncNote,
    syncTaxes,
    checks,
    runningChecks,
    checksError,
    runChecks,
    adoptingGstin,
    adoptGstinError,
    adoptZohoGstin,
  };
};
